// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyProject } from "@/types";
import { pressToOpen, readLangValue, rightClickToOpen } from "@/utils";
import Icon from "@/UI/atoms/Icon";
import StatusChip from "@/UI/atoms/StatusChip";
import "../selection.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    project: TyProject;
    onOpen: () => void;
    onActions: (anchor: DOMRect) => void;
    /** Picked by the bulk bar. Draws the ring; the screen decides what pressing the card then means. */
    isSelected?: boolean;
    /**
     * The bar is up. Every other control on the card is **gone, not disabled** — a half-live card
     * looks pressable and does something nobody asked for — and the menu button's place is taken by
     * the tick, so nothing moves and nothing overlaps.
     */
    isPicking?: boolean;
};

/**
 * One project. The cover and the counts both come from the list endpoint, which resolves the whole
 * page in one pass rather than asking per card — the picture is the first of the project's gallery
 * **files**, as a thumbnail.
 *
 * The whole card opens it. Hunting for the one word that was a link is a tax on every row of every
 * list, and the more-actions button is the single thing on it that means something else.
 */
export default function ProjectCard({ project, onOpen, onActions, isSelected, isPicking }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const name = readLangValue(project.name, lang) || t.properties.unnamed;
    const cover = project.cover ?? project.gallery?.[0] ?? null; // the list sends one; a record screen's project has `gallery` instead
    const counts = project.counts;

    return (
        <article
            className={`project-card pick${isSelected ? " pick--on" : ""}${isPicking ? " pick--picking" : ""}`}
            role="button"
            tabIndex={0}
            aria-pressed={isSelected}
            onClick={onOpen}
            onKeyDown={pressToOpen(onOpen)}
            onContextMenu={isPicking ? undefined : rightClickToOpen(onActions)}
        >
            <div className="project-card__cover">
                {cover ? <img className="project-card__image" src={cover} alt="" loading="lazy" /> : <Icon name="properties" size={30} />}

                {project.stage && <span className="project-card__stage">{t.properties.stages[project.stage]}</span>}
                {project.incomplete && <span className="project-card__flag" title={t.properties.incomplete} />}
            </div>

            <div className="project-card__body">
                <div className="project-card__identity">
                    <span className="project-card__name">{name}</span>
                    <span className="project-card__meta">
                        {project.city || t.properties.notSet}
                        {counts ? ` · ${counts.units} ${t.properties.counting.units}` : ""}
                    </span>
                </div>

                {/* The one place on the card that is **not** the card: it opens a menu instead —
                    until the bar is up, when the card means one thing and this is the tick. */}
                {isPicking ? (
                    <span className={`pick__box${isSelected ? " pick__box--on" : ""}`} aria-hidden="true">
                        <Icon name="check" size={18} />
                    </span>
                ) : (
                    <button
                        type="button"
                        className="project-card__more"
                        onClick={(event) => {
                            event.stopPropagation();
                            onActions(event.currentTarget.getBoundingClientRect());
                        }}
                        aria-label={t.common.more}
                    >
                        <Icon name="more" size={18} />
                    </button>
                )}
            </div>

            <div className="project-card__chips">
                {counts && counts.available > 0 && (
                    <StatusChip size="sm" tone="available" label={`${counts.available} ${t.properties.counting.available}`} />
                )}
                {project.types.slice(0, 2).map((type) => (
                    <StatusChip key={type} size="sm" tone="neutral" label={t.properties.unitTypes[type]} />
                ))}
                {project.isPublic && <StatusChip size="sm" tone="accent" label={t.properties.fields.isPublic} />}
            </div>
        </article>
    );
}
